import React, { useState } from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import './TAGEAnswerSheet.css';

const TAGEAnswerSheet = ({
  answers,
  onAnswerChange,
  currentQuestion,
  subTests,
  title = 'FEUILLE DE RÉPONSE',
  onQuestionClick,
}) => {
  const options = ['A', 'B', 'C', 'D', 'E'];
  const [showFullInstructions, setShowFullInstructions] = useState(false);

  const handleBarClick = (questionNum, option, bar) => {
    const answer = answers[questionNum] || {
      topBars: [], // Array des lettres dont la barre du haut est cochée
      bottomBars: [], // Array des lettres dont la barre du bas est cochée
      modified: false, // true après avoir coché une barre du bas
    };

    const newAnswer = {
      topBars: [...answer.topBars],
      bottomBars: [...answer.bottomBars],
      modified: answer.modified,
    };

    // Cas 1 : Aucune barre cochée encore → cocher barre du haut
    if (newAnswer.topBars.length === 0 && newAnswer.bottomBars.length === 0) {
      if (bar === 'top' && !newAnswer.topBars.includes(option)) {
        newAnswer.topBars.push(option);
      }
    }
    // Cas 2 : Au moins une barre du haut cochée, pas de barre du bas
    else if (newAnswer.bottomBars.length === 0) {
      if (bar === 'top') {
        // Continuer à cocher des barres du haut
        if (!newAnswer.topBars.includes(option)) {
          newAnswer.topBars.push(option);
        }
      } else {
        // Première barre du bas = modification
        if (!newAnswer.bottomBars.includes(option)) {
          newAnswer.bottomBars.push(option);
          newAnswer.modified = true;
        }
      }
    }
    // Cas 3 : Au moins une barre du bas cochée (modification effectuée)
    // → Tout autre clic déclenche l'annulation (cocher TOUTES les barres)
    else if (newAnswer.modified) {
      // Annuler = cocher toutes les barres (5 haut + 5 bas)
      newAnswer.topBars = ['A', 'B', 'C', 'D', 'E'];
      newAnswer.bottomBars = ['A', 'B', 'C', 'D', 'E'];
    }

    onAnswerChange(questionNum, newAnswer);
  };

  // Déterminer la réponse finale
  const getFinalAnswer = (questionNum) => {
    const answer = answers[questionNum];
    if (!answer) return null;

    // Si toutes les barres sont cochées → annulation
    if (isQuestionCancelled(questionNum)) {
      return 'cancelled';
    }

    // Si au moins une barre du bas est cochée, c'est la réponse finale (modification)
    if (answer.bottomBars && answer.bottomBars.length > 0) {
      return answer.bottomBars[0]; // Première barre du bas cochée
    }

    // Sinon, si au moins une barre du haut est cochée, c'est la réponse
    if (answer.topBars && answer.topBars.length > 0) {
      return answer.topBars[0]; // Première barre du haut cochée
    }

    return null;
  };

  const isQuestionCancelled = (questionNum) => {
    const answer = answers[questionNum];
    if (!answer) return false;

    // Annulation : TOUTES les 10 barres cochées (5 haut + 5 bas)
    // Cela se produit automatiquement après un 3ème clic (après avoir coché 1 barre haut + 1 barre bas)
    const allTopBarsFilled = answer.topBars && answer.topBars.length === 5;
    const allBottomBarsFilled =
      answer.bottomBars && answer.bottomBars.length === 5;

    return allTopBarsFilled && allBottomBarsFilled;
  };

  return (
    <div className="tage-answer-sheet">
      {/* Header */}
      <div className="answer-sheet-header">
        <h2>{title}</h2>
      </div>

      {/* Instructions Section - Mode d'emploi compact avec toggle */}
      <div className="answer-sheet-how-to-use-compact">
        <div className="how-to-compact-header">
          <h3>📋 MODE D'EMPLOI</h3>
          <button
            className="toggle-instructions-btn"
            onClick={() => setShowFullInstructions(!showFullInstructions)}
          >
            {showFullInstructions ? (
              <>
                Masquer <FiChevronUp size={18} />
              </>
            ) : (
              <>
                Lire les instructions <FiChevronDown size={18} />
              </>
            )}
          </button>
        </div>

        {/* Version compacte (toujours visible) */}
        <div className="how-to-compact-summary">
          <div className="summary-steps">
            <span className="summary-step">
              <span className="step-badge">1</span> Barre du haut = Répondre
            </span>
            <span className="summary-step">
              <span className="step-badge">2</span> Barre du bas = Modifier
            </span>
            <span className="summary-step warning">
              <span className="step-badge">3</span> 3ème clic = Annulation
            </span>
          </div>
        </div>

        {/* Version détaillée (conditionnelle) */}
        {showFullInstructions && (
          <div className="how-to-detailed">
            <div className="how-to-steps">
              <div className="step-card">
                <div className="step-number">1</div>
                <div className="step-content">
                  <strong>Première action : Répondre</strong>
                  <p>
                    Cliquez sur la <strong>barre du haut</strong> de votre choix
                    (ex: A)
                  </p>
                  <p className="step-note">
                    ✓ Vous pouvez cocher plusieurs barres du haut si vous
                    hésitez
                  </p>
                </div>
              </div>

              <div className="step-card">
                <div className="step-number">2</div>
                <div className="step-content">
                  <strong>Deuxième action : Modifier (optionnel)</strong>
                  <p>
                    Cliquez sur la <strong>barre du bas</strong> d'une{' '}
                    <strong>autre</strong> lettre (ex: B)
                  </p>
                  <p className="step-note">
                    ✓ La barre du bas devient votre réponse finale
                  </p>
                </div>
              </div>

              <div className="step-card step-warning">
                <div className="step-number">3</div>
                <div className="step-content">
                  <strong>Troisième action : Annulation automatique</strong>
                  <p>
                    Si vous cliquez sur{' '}
                    <strong>n'importe quelle autre barre</strong>
                  </p>
                  <p className="step-note">
                    ⚠️ TOUTES les barres s'activent = Réponse annulée (0 point)
                  </p>
                </div>
              </div>
            </div>
            <div className="important-note">
              <strong>⚠️ IMPORTANT</strong>
              <p>
                Une fois une barre cochée, vous ne pouvez PAS la décocher.
                Maximum 3 actions par question.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Instructions Section - Exemples visuels (toujours visibles) */}
      <div className="answer-sheet-instructions">
        <h4>Exemples visuels</h4>
        <div className="instructions-row">
          <div className="instruction-item">
            <div className="instruction-icon">
              <div className="answer-option-example-bars">
                {options.map((opt, idx) => (
                  <div key={opt} className="option-with-bars">
                    <div
                      className={`bar-top ${idx === 0 ? 'bar-filled' : ''}`}
                    ></div>
                    <span className="option-letter">{opt}</span>
                    <div className="bar-bottom"></div>
                  </div>
                ))}
              </div>
            </div>
            <span className="instruction-label">Pour répondre A</span>
          </div>

          <div className="instruction-item">
            <div className="instruction-icon">
              <div className="answer-option-example-bars">
                {options.map((opt, idx) => (
                  <div key={opt} className="option-with-bars">
                    <div
                      className={`bar-top ${idx === 0 ? 'bar-filled' : ''}`}
                    ></div>
                    <span className="option-letter">{opt}</span>
                    <div
                      className={`bar-bottom ${idx === 1 ? 'bar-filled' : ''}`}
                    ></div>
                  </div>
                ))}
              </div>
            </div>
            <span className="instruction-label">Pour modifier A en B¹</span>
          </div>

          <div className="instruction-item">
            <div className="instruction-icon">
              <div className="answer-option-example-bars">
                {options.map((opt) => (
                  <div key={opt} className="option-with-bars">
                    <div className="bar-top bar-filled"></div>
                    <span className="option-letter">{opt}</span>
                    <div className="bar-bottom bar-filled"></div>
                  </div>
                ))}
              </div>
            </div>
            <span className="instruction-label">Annulé (3ème clic)</span>
          </div>

          <div className="instruction-item">
            <div className="instruction-icon">
              <div className="answer-option-example-bars">
                {options.map((opt) => (
                  <div key={opt} className="option-with-bars">
                    <div className="bar-top"></div>
                    <span className="option-letter">{opt}</span>
                    <div className="bar-bottom"></div>
                  </div>
                ))}
              </div>
            </div>
            <span className="instruction-label">Aucune réponse</span>
          </div>
        </div>
        <div className="instruction-note">¹ B devient la réponse valide</div>
      </div>

      {/* Answer Grid */}
      <div className="answer-sheet-grid">
        {subTests && subTests.length > 0 ? (
          subTests.map((subTest) => (
            <div key={subTest.id} className="sub-test-section">
              <div className="sub-test-header">
                <span className="sub-test-label">Sous test {subTest.id}:</span>
                <span className="sub-test-name">{subTest.name}</span>
              </div>

              <div className="questions-grid">
                {Array.from(
                  { length: subTest.end - subTest.start + 1 },
                  (_, i) => i + subTest.start
                ).map((questionNum) => {
                  const answer = answers[questionNum] || {};
                  const isCurrentQuestion = questionNum === currentQuestion;
                  const isCancelled = isQuestionCancelled(questionNum);
                  const isModified = answer.modified || false;

                  return (
                    <div
                      key={questionNum}
                      className={`question-row ${isCurrentQuestion ? 'current' : ''} ${isCancelled ? 'cancelled' : ''} ${isModified ? 'modified' : ''}`}
                      style={{
                        cursor: onQuestionClick ? 'pointer' : 'default',
                      }}
                    >
                      <div
                        className="question-number"
                        onClick={() =>
                          onQuestionClick && onQuestionClick(questionNum)
                        }
                        title={`Aller à la question ${questionNum}`}
                      >
                        {questionNum}
                      </div>

                      {/* Options avec barres cliquables */}
                      <div className="answer-options-row">
                        {options.map((option) => {
                          const isTopBarFilled =
                            answer.topBars && answer.topBars.includes(option);
                          const isBottomBarFilled =
                            answer.bottomBars &&
                            answer.bottomBars.includes(option);

                          return (
                            <div
                              key={`${questionNum}-${option}`}
                              className="answer-option-with-bars"
                            >
                              <button
                                className={`clickable-bar bar-top ${isTopBarFilled ? 'bar-filled' : ''}`}
                                onClick={() =>
                                  handleBarClick(questionNum, option, 'top')
                                }
                                title={`Sélectionner ${option} (réponse initiale)`}
                              />
                              <span className="option-letter">{option}</span>
                              <button
                                className={`clickable-bar bar-bottom ${isBottomBarFilled ? 'bar-filled' : ''}`}
                                onClick={() =>
                                  handleBarClick(questionNum, option, 'bottom')
                                }
                                title={`Sélectionner ${option} (modification)`}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        ) : (
          <div className="answer-sheet-empty">
            <p>Aucune question disponible</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TAGEAnswerSheet;
